/**
 * Lewis Legacy Of Love: front-end entry (bundled by esbuild into /public/dist).
 *
 * Always on:          header, menu, anchors, form enhancement, lightbox, slider
 * Motion allowed:     loader, Lenis, reveals, parallax, marquee, pinned steps, cursor,
 *                     magnetic buttons, card tilt, page transitions, colour shift
 * Reduced motion:     none of the above motion; content is simply shown
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initLenis, initAnchors } from './lenis.js';
import { initHeader } from './header.js';
import { heroIntro, initReveals, initParallax, initHeroPointer, initBgShift, initSafety } from './reveal.js';
import { initMarquee } from './marquee.js';
import { initMagnetic, initTilt } from './magnetic.js';
import { initCursor } from './cursor.js';
import { initHorizontal } from './horizontal.js';
import { initLightbox, initSlider } from './gallery.js';
import { initTransitions } from './transitions.js';
import { initForms } from './form.js';
import { initApplyForms } from './apply.js';

gsap.registerPlugin(ScrollTrigger);
window.__ScrollTrigger = ScrollTrigger; // exposed for the QA scripts

const html = document.documentElement;
const reduced = html.classList.contains('reduced-motion') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function runLoader() {
  const loader = document.querySelector('[data-loader]');
  if (!loader || html.classList.contains('no-loader')) return Promise.resolve();
  loader.style.animation = 'none'; // JS is running, so the CSS failsafe is not needed
  return new Promise((resolve) => {
    gsap.to(loader, {
      yPercent: -100,
      duration: 0.55,
      ease: 'power4.inOut',
      delay: 0.8,
      onStart: () => setTimeout(resolve, 150),
      onComplete: () => loader.classList.add('is-done'),
    });
  });
}

// Hand control back to the browser between setup chunks, so no single task blocks input for long.
const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 0));
// Below-the-fold effects wait for a quiet moment so they never compete with the first paint.
const whenIdle = () => new Promise((resolve) => ('requestIdleCallback' in window ? requestIdleCallback(resolve, { timeout: 1200 }) : setTimeout(resolve, 200)));

async function boot() {
  window.__llolReady = true; // JS is running: cancel the head script's show-everything failsafe
  performance.mark('llol:start'); // user-timing marks show each setup step in DevTools/Lighthouse
  initHeader();
  initAnchors();
  initForms();
  initApplyForms();
  initLightbox();
  initSlider();
  performance.mark('llol:base');

  if (reduced) return;

  initLenis();
  initTransitions();
  runLoader().then(() => heroIntro());
  performance.mark('llol:intro');

  await yieldToMain();
  // Pins first, so every trigger created afterwards measures the pin spacer correctly.
  initHorizontal();
  initReveals();
  performance.mark('llol:reveals');

  await whenIdle();
  initParallax();
  initSafety();
  initBgShift();
  initMarquee();
  performance.mark('llol:scroll-effects');

  if (finePointer) {
    await yieldToMain();
    initCursor();
    initMagnetic();
    initTilt();
    initHeroPointer();
  performance.mark('llol:pointer');
  }

  // ScrollTrigger re-measures on load by itself; web fonts arriving later change text metrics,
  // so refresh once more only if they were still loading when we set up.
  if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => ScrollTrigger.refresh());
}

boot();
